import { FlatTreeControl } from '@angular/cdk/tree';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  Input,
  output,
} from '@angular/core';
import { NzIconModule } from 'ng-zorro-antd/icon';
import {
  NzTreeFlatDataSource,
  NzTreeFlattener,
  NzTreeViewModule,
} from 'ng-zorro-antd/tree-view';
import { FlatNode, TreeNode } from './tree.model';
import { TreeNodeOptionComponent } from './tree-node-option.component';

/**
 * TreeComponent
 * This component renders a customizable hierarchical tree structure,
 * displaying nodes with the ability to expand and collapse, and optional custom templates for each node.
 * It uses Angular's CDK Tree structure for managing hierarchical data and rendering.
 *
 * @template T - The type of data for each tree node.
 */
@Component({
  selector: 'ui-tree',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzIconModule, NzTreeViewModule, TreeNodeOptionComponent],
  template: `
    <nz-tree-view [nzTreeControl]="treeControl" [nzDataSource]="dataSource">
      <nz-tree-node *nzTreeNodeDef="let node" nzTreeNodeIndentLine>
        <nz-tree-node-option
          [nzDisabled]="node.disabled"
          (nzClick)="handleNodeOption(node)"
        >
          <ui-tree-node-option [node]="node" />
        </nz-tree-node-option>
      </nz-tree-node>

      <nz-tree-node
        *nzTreeNodeDef="let node; when: hasChild"
        nzTreeNodeIndentLine
      >
        @if (canExpand()) {
          <nz-tree-node-toggle>
            <span
              nz-icon
              [nzType]="
                node?.expandIcon ||
                (treeControl.isExpanded(node) ? 'minus-square' : 'plus-square')
              "
              nzTheme="outline"
            ></span>
          </nz-tree-node-toggle>
        }
        <nz-tree-node-option
          [nzDisabled]="node.disabled"
          (nzClick)="handleNodeOption(node)"
        >
          <ui-tree-node-option [node]="node" />
        </nz-tree-node-option>
      </nz-tree-node>
    </nz-tree-view>
  `,
})
export class TreeComponent {
  canExpand = input<boolean>(true);

  private _data: TreeNode[] = [];
  private initialExpanded: boolean = false;

  onNode = output<FlatNode>();

  /**
   * Data for the tree nodes.
   *
   * @param data - An array of `TreeNode` objects representing the tree structure.
   * Updates the data source when the input data changes.
   */
  @Input()
  set data(data: TreeNode[]) {
    this._data = data;
    this.updateDataSource();
  }

  get data(): TreeNode[] {
    return this._data;
  }

  private transformer = (node: TreeNode, level: number): FlatNode => ({
    expandable: !!node.children && node.children.length > 0,
    disabled: !!node.disabled,
    name: node.name,
    icon: node.icon,
    expandIcon: node.expandIcon,
    template: node.template,
    context: node.context,
    level,
  });

  /**
   * Control for managing the expansion state and levels of the tree nodes.
   *
   * The tree control is based on the level and expandability of nodes.
   */
  treeControl = new FlatTreeControl<FlatNode>(
    (node) => node.level,
    (node) => node.expandable,
  );

  /**
   * Flattener for converting hierarchical data into a flat structure.
   *
   * @param transformer - A function that transforms a `TreeNode` into a `FlatNode`.
   */
  treeFlattener = new NzTreeFlattener(
    this.transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children,
  );

  /**
   * The data source for the tree, using the `NzTreeFlatDataSource` with the flattener.
   *
   * This binds the flat data to the tree control, enabling the tree to be rendered correctly.
   */
  dataSource = new NzTreeFlatDataSource(this.treeControl, this.treeFlattener);

  /**
   * Updates the data source with the current tree data.
   *
   * This function is called whenever the input data changes to ensure the tree is correctly rendered.
   */
  private updateDataSource(): void {
    if (this._data && this._data.length > 0) {
      this.dataSource.setData(this._data);
      this.initialExpanded = false;
    }
  }

  /**
   * Checks if a given node has child nodes (i.e., is expandable).
   *
   * @param _: The index of the node in the flat data structure (not used here).
   * @param node - The flat node being checked.
   * @returns Whether the node has child nodes (is expandable).
   */
  hasChild = (_: number, node: FlatNode): boolean => node.expandable;

  handleNodeOption(node: FlatNode) {
    // Reset all nodes
    this.resetSelections(this._data);

    // Set `selected` and `context.selected` for the provided node
    node.selected = true;
    if (node.context) {
      node.context.selected = true;
    }

    // Update the data source
    this.updateDataSource();
    this.onNode.emit(node);
  }

  // Recursively traverse and reset `selected` and `context.selected` for all nodes in `this._data`
  resetSelections(data: any[]) {
    for (const item of data) {
      item.selected = false;
      if (item.context) {
        item.context.selected = false;
      }
      if (item.children) {
        this.resetSelections(item.children);
      }
    }
  }

  /**
   * Expands all nodes in the tree after the view is initialized.
   *
   * This ensures that all nodes are expanded by default when the tree is first displayed.
   */
  ngAfterViewChecked(): void {
    if (!this.initialExpanded) {
      this.treeControl.expandAll();
      this.initialExpanded = true;
    }
  }
}
